import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { UserServices } from "../../../services/user/user.services";
import { addUser } from "../../../redux/user/userslice";
import { useNavigate } from "react-router-dom";
import { handleNotify, TOASTER_POSITION, TOASTER_TYPE } from "../../common/notification/toaster_notify.component";

const ProfileComponent = () => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);
    const navigate = useNavigate();

    const formik = useFormik({
        initialValues: {
            id: "",
            username: "",
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            gender: "",
            userPassword: "",
            confirmPassword: ""
        },
        validationSchema: Yup.object({
            username: Yup.string().required("Username is required"),
            email: Yup.string().email("Invalid email").required("Email is required"),
            phone: Yup.string().required("Phone is required"),
            gender: Yup.string().required("Gender is required"),
            userPassword: Yup.string()
                .min(6, "Password must be at least 6 characters")
                .notRequired(),

            confirmPassword: Yup.string()
                .oneOf([Yup.ref("userPassword"), ""], "Passwords must match")
                .oneOf([Yup.ref("userPassword"), null], "Passwords must match"),

        }),
        onSubmit: async (values) => {
            try {
                if (!user || !user.userId) {
                    handleNotify("User ID missing. Cannot update profile.", TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
                    return;
                }
                const updatedData = {};

                if (values.username.trim() !== (user.username || "").trim()) updatedData.username = values.username;
                if (values.firstName.trim() !== (user.firstName || "").trim()) updatedData.firstName = values.firstName;
                if (values.lastName.trim() !== (user.lastName || "").trim()) updatedData.lastName = values.lastName;
                if (values.email.trim() !== (user.email || "").trim()) updatedData.email = values.email;
                if (values.phone.trim() !== (user.phone || "").trim()) updatedData.phone = values.phone;
                if (values.gender !== (user.gender || "")) updatedData.gender = values.gender;
                if (values.userPassword) {
                    if (values.userPassword === values.confirmPassword) {
                        updatedData.userPassword = values.userPassword;
                    } else {
                        handleNotify("Passwords must match", TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
                        return;
                    }
                }
                if (Object.keys(updatedData).length === 0) {
                    handleNotify("Please update profile data.", TOASTER_TYPE.WARNING, TOASTER_POSITION.TOP_RIGHT);
                    return;
                }
                const response = await UserServices.updateUserById(user.userId, updatedData);

                if (response) {
                    dispatch(addUser(response));
                }
            } catch (error) {
                handleNotify("Profile update failed!", TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
            }
        }
    });

    useEffect(() => {
        if (user) {
            formik.setValues({
                username: user.username || "",
                firstName: user.firstName || "",
                lastName: user.lastName || "",
                email: user.email || "",
                phone: user.phone || "",
                gender: user.gender || "",
                userPassword: "",
                confirmPassword: ""

            });
        }
    }, [user]);

    const { values, handleChange, handleSubmit, errors, touched } = formik;

    const handleBackClick = () => {
        navigate('/dashboard');
    };

    return (
        <div className="page-content">
            <div className="container">
                <div className="main-body">
                    <div className="row">
                        {/* User Profile read-only */}
                        <div className="col-lg-4">
                            <h3 className="text-primary m-3 d-flex">User Profile</h3>
                            <div className="card">
                                <div className="card-body mt-5">
                                    <div className="d-flex flex-column align-items-center text-center">
                                        <img
                                            src="\horizontal\assets\images\user-avatar.png"
                                            alt="admin"
                                            className="rounded-circle p-1"
                                        >
                                        </img>
                                        {/* <i className="bx bx-user-circle bx-lg text-primary" /> */}
                                        <div className="mt-3">
                                            <h4>{values.username}</h4>
                                            <p className="text-secondary mb-1">{values.userRole}</p>
                                        </div>
                                    </div>
                                    <hr className="my-4" />
                                    <ul className="list-group list-group-flush">
                                        <li className="list-group-item d-flex justify-content-between">
                                            <h6 className="mb-0">Full Name</h6>
                                            <span className="text-secondary">{values.username}</span>
                                        </li>
                                        <li className="list-group-item d-flex justify-content-between">
                                            <h6 className="mb-0">Email</h6>
                                            <span className="text-secondary">{values.email}</span>
                                        </li>
                                        <li className="list-group-item d-flex justify-content-between">
                                            <h6 className="mb-0">Gender</h6>
                                            <span className="text-secondary">{values.gender}</span>
                                        </li>
                                        <li className="list-group-item d-flex justify-content-between">
                                            <h6 className="mb-0">Phone#</h6>
                                            <span className="text-secondary">{values.phone}</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-8">
                            <h3 className="text-primary m-3 d-flex">Profile Update</h3>
                            <div className="card">
                                <div className="card-body mt-3">
                                    <form onSubmit={handleSubmit}>
                                        <h6 className="mb-0 text-danger"></h6>
                                        {/* Username */}
                                        <div className="row mb-1">
                                            <div className="col-sm-3 mb-1">
                                                <h6 className="mb-0 d-flex">User Name</h6>
                                            </div>
                                            <div className="col-sm-9 mb-2">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="username"
                                                    value={values.username}
                                                    onChange={handleChange}
                                                />
                                                {touched.username && errors.username && <small className="text-danger">{errors.username}</small>}
                                            </div>
                                        </div>

                                        {/* Email */}
                                        <div className="row mb-1">
                                            <div className="col-sm-3 mb-2">
                                                <h6 className="mb-0 d-flex">Email</h6>
                                            </div>
                                            <div className="col-sm-9 mb-2">
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    name="email"
                                                    value={values.email}
                                                    onChange={handleChange}
                                                />
                                                {touched.email && errors.email && <small className="text-danger">{errors.email}</small>}
                                            </div>
                                        </div>

                                        <div className="row mb-1">
                                            <div className="col-sm-3 mb-2">
                                                <h6 className="mb-0 d-flex">Full Name</h6>
                                            </div>
                                            <div className="col-sm-5 mb-2">
                                                <input
                                                    type="firstName"
                                                    className="form-control"
                                                    name="firstName"
                                                    value={values.firstName}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                            <div className="col-sm-4 mb-2">
                                                <input
                                                    type="lastName"
                                                    className="form-control"
                                                    name="lastName"
                                                    value={values.lastName}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                        {/* Gender */}
                                        <div className="row mb-2">
                                            <div className="col-sm-3 mb-2">
                                                <h6 className="mb-0 d-flex">Gender</h6>
                                            </div>
                                            <div className="col-sm-9 mb-2 d-flex align-items-center">
                                                <div className="form-check form-check-inline me-3">
                                                    <input
                                                        type="radio"
                                                        className="form-check-input"
                                                        name="gender"
                                                        value="Male"
                                                        checked={values.gender === "Male"}
                                                        onChange={handleChange}
                                                    />
                                                    <label className="form-check-label">Male</label>
                                                </div>
                                                <div className="form-check form-check-inline">
                                                    <input
                                                        type="radio"
                                                        className="form-check-input"
                                                        name="gender"
                                                        value="Female"
                                                        checked={values.gender === "Female"}
                                                        onChange={handleChange}
                                                    />
                                                    <label className="form-check-label">Female</label>
                                                </div>
                                                {touched.gender && errors.gender && <small className="text-danger ms-3">{errors.gender}</small>}
                                            </div>
                                        </div>

                                        {/* Phone */}
                                        <div className="row mb-1">
                                            <div className="col-sm-3 mb-2">
                                                <h6 className="mb-0 d-flex">Phone#</h6>
                                            </div>
                                            <div className="col-sm-9 mb-2">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="phone"
                                                    value={values.phone}
                                                    onChange={handleChange}
                                                />
                                                {touched.phone && errors.phone && <small className="text-danger">{errors.phone}</small>}
                                            </div>
                                        </div>

                                        {/* Info Alert Message */}
                                        <div className="row mb-2">
                                            <div className="col-sm-3"></div>
                                            <div className="col-sm-12">
                                                <div className="alert alert-info bg-info text-white border-0 d-flex justify-content-between align-items-center py-2 px-3 fade show">
                                                    <div>
                                                        Leave the password field empty if you don't want to change.
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn-close btn-close-white"
                                                        data-bs-dismiss="alert"
                                                        aria-label="Close"
                                                    ></button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Passwords */}
                                        <div className="row mb-2">
                                            <div className="col-sm-3 mb-2">
                                                <h6 className="mb-0 d-flex">Password</h6>
                                            </div>
                                            <div className="col-sm-5 mb-2">
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    name="userPassword"
                                                    placeholder="Password"
                                                    value={values.userPassword}
                                                    onChange={handleChange}
                                                />
                                                {touched.userPassword && errors.userPassword && (
                                                    <small className="text-danger">{errors.userPassword}</small>
                                                )}
                                            </div>
                                            <div className="col-sm-4 mb-2">
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    name="confirmPassword"
                                                    placeholder="Confirm Password"
                                                    value={values.confirmPassword}
                                                    onChange={handleChange}
                                                />
                                                {values.confirmPassword && values.userPassword !== values.confirmPassword ? (
                                                    <small className="text-danger">Passwords must match</small>
                                                ) : touched.confirmPassword && errors.confirmPassword ? (
                                                    <small className="text-danger">{errors.confirmPassword}</small>
                                                ) : null}
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <hr />
                                        <div className="text-end">
                                            <button type="button" className="btn btn-outline-secondary me-2" onClick={handleBackClick}>
                                                Back
                                            </button>
                                            <button type="submit" className="btn btn-primary">
                                                Save
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div >
        </div >
    );
};

export default ProfileComponent;